/**
 * Properties Tab Component
 * Inspector for editing selected control properties
 */

import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormControl, FieldValidation, ControlStyleSet } from '@atlas-forms/types-js';
import type { DesignerFormControl, DesignerControlPatch } from '../../types/DesignerFormControl';
import { StyleBuilderPanel, StyleSlotRow } from '../StyleBuilderPanel';
import type { StyleBuilderPanelProps } from '../StyleBuilderPanel';
import { getApplicableSlots } from '@atlas-forms/player-components-react';
import { getDefaultConfig } from '../../utils/controlUtils';
import { FormContainerPropertyPanel } from '@atlas-forms/controls-form-container-react';
import { FullscreenIcon } from '../Icons/FullscreenIcon';
import { ClearableInput } from './ClearableInput';
import {
  BasicPropertiesIcon,
  StateIcon,
  GridLayoutIcon,
  ValidationIcon,
  DefaultValueIcon,
  StylingIcon,
  AnimationsIcon,
  ScriptsIcon,
  ContainerIcon,
  ActionsIcon,
  I18nIcon,
  IconsIcon,
  AccessibilityIcon,
  SecurityIcon,
  ControlConfigIcon,
  OptionsIcon,
  NumberConfigIcon,
  DateConfigIcon,
  MediaConfigIcon,
  DocumentConfigIcon,
} from '../Icons/PropertiesIconSet';
import {
  SearchIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExpandPanelIcon,
  CollapsePanelIcon
} from '@atlas-forms/ui-components-react';
import { PropertiesModal } from './PropertiesModal';
import { ConnectedFormLookupField } from '../../form-lookup';
import { ControlSelect } from '../ControlSelect';
import { PropertyEditorRegistry } from '../../registry/PropertyEditorRegistry';
import { DefaultPropertyEditor } from '../PropertyEditors/DefaultPropertyEditor';
import { loadBuiltInEditors } from '../../registry/loadBuiltInEditors';
import '../../styles/tabs/PropertiesTab.css';

// Ensure built-in editors are loaded synchronously on first import
// This guarantees editors are available even if the async auto-init hasn't completed
try {
  if (!PropertyEditorRegistry.isInitialized()) {
    loadBuiltInEditors();
  }
} catch {
  // Already initialized or registration error — safe to ignore
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface OptionItem {
  value: string;
  label: string;
  disabled?: boolean;
}

// ─── Options Editor ──────────────────────────────────────────────────────────
// Phase 4: Enhanced with column headers for better UX

interface OptionsEditorProps {
  options: OptionItem[];
  onChange: (options: OptionItem[]) => void;
}

function OptionsEditor({ options, onChange }: OptionsEditorProps) {
  const handleChange = (index: number, field: keyof OptionItem, val: string | boolean) => {
    const updated = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt,
    );
    onChange(updated);
  };

  const handleAdd = () => {
    onChange([...options, { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` }]);
  };

  const handleRemove = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <div className="af-properties-tab__options-editor">
      {options.length === 0 && (
        <p className="af-properties-tab__options-empty">No options yet. Add one below.</p>
      )}
      {options.length > 0 && (
        <>
          {/* Column Headers */}
          <div className="af-properties-tab__option-header">
            <span className="af-properties-tab__option-header-value">Value</span>
            <span className="af-properties-tab__option-header-label">Label</span>
            <span className="af-properties-tab__option-header-disabled">Disabled</span>
            <span className="af-properties-tab__option-header-actions">Action</span>
          </div>
          {/* Option Rows */}
          {options.map((opt, i) => (
            <div key={i} className="af-properties-tab__option-row">
              <input
                type="text"
                value={opt.value}
                onChange={(e) => handleChange(i, 'value', e.target.value)}
                className="af-properties-tab__input af-properties-tab__option-value"
                placeholder="value"
                title="Option value (stored in form data)"
                aria-label={`Option ${i + 1} value`}
              />
              <input
                type="text"
                value={opt.label}
                onChange={(e) => handleChange(i, 'label', e.target.value)}
                className="af-properties-tab__input af-properties-tab__option-label"
                placeholder="label"
                title="Option label (displayed to user)"
                aria-label={`Option ${i + 1} label`}
              />
              <label className="af-properties-tab__option-disabled-label" title="Disable this option">
                <input
                  type="checkbox"
                  checked={opt.disabled || false}
                  onChange={(e) => handleChange(i, 'disabled', e.target.checked)}
                  aria-label={`Disable option ${i + 1}`}
                />
                <span className="af-properties-tab__option-disabled-text">Off</span>
              </label>
              <button
                type="button"
                className="af-properties-tab__option-remove"
                onClick={() => handleRemove(i)}
                aria-label={`Remove option ${i + 1}`}
                title="Remove this option"
              >
                <CloseIcon width={12} height={12} />
              </button>
            </div>
          ))}
        </>
      )}
      <button type="button" className="af-properties-tab__add-option-btn" onClick={handleAdd}>
        + Add Option
      </button>
    </div>
  );
}

// ─── ControlConfig ────────────────────────────────────────────────────────────

/**
 * ControlConfig Component (Refactored)
 * Delegates to PropertyEditorRegistry for control-type-specific configuration
 *
 * BEFORE: 430 lines of if/else if statements for 17 control types
 * AFTER: 15 lines using registry pattern
 *
 * Design:
 * - Single responsibility: delegate to appropriate editor
 * - Open/closed: new editors can be registered without modifying this component
 * - Type safe: uses PropertyEditor interface
 * - Graceful fallback: shows helpful message for unknown control types
 */

interface ControlConfigProps {
  control: FormControl;
  onConfigChange: (key: string, value: unknown) => void;
  onBasicChange: (key: keyof FormControl, value: unknown) => void;
  onValidationChange?: (key: keyof FieldValidation, value: unknown) => void;
  onControlChange: (updates: Partial<FormControl>) => void;
}

function ControlConfig({
  control,
  onConfigChange,
  onBasicChange,
  onValidationChange,
  onControlChange,
}: ControlConfigProps) {
  // Get editor for this control type from registry
  const editor = PropertyEditorRegistry.get(control.type);

  // If no specific editor registered, use default fallback
  const editorToUse = editor || DefaultPropertyEditor;

  // Render editor with handlers
  return editorToUse.render({
    control,
    onConfigChange,
    onBasicChange,
    onValidationChange: onValidationChange || (() => {}),
    onControlChange,
  });
}

export interface PropertiesTabProps {
  control: DesignerFormControl | null;
  onUpdateControl: (id: string, updates: DesignerControlPatch) => void;
  /** When true, hides the fullscreen button (used when already inside PropertiesModal) */
  hideFullscreen?: boolean;
  /** Form's column count (Form Properties > Columns); used to warn when a width needs 2+ columns */
  numberOfColumns?: number;
}

// ─── Accordion Section Component with LocalStorage Persistence ────────────

interface AccordionSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
  icon?: React.ReactNode;
}

function AccordionSection({ title, description, children, defaultOpen = false, storageKey, icon }: AccordionSectionProps) {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (storageKey) {
      try {
        const stored = localStorage.getItem(storageKey);
        return stored !== null ? JSON.parse(stored) : defaultOpen;
      } catch {
        return defaultOpen;
      }
    }
    return defaultOpen;
  });

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch (e) {
          console.warn('Failed to save accordion state to localStorage', e);
        }
      }
      return next;
    });
  }, [storageKey]);

  return (
    <div className="af-properties-tab__accordion-section">
      <button
        className={`af-properties-tab__accordion-header ${isOpen ? 'af-properties-tab__accordion-header--open' : ''}`}
        onClick={handleToggle}
        type="button"
      >
        <span className="af-properties-tab__accordion-toggle">
          {isOpen ? <ChevronDownIcon width={12} height={12} /> : <ChevronRightIcon width={12} height={12} />}
        </span>
        {icon && <span className="af-properties-tab__accordion-icon">{icon}</span>}
        <div className="af-properties-tab__accordion-header-content">
          <h4 className="af-properties-tab__accordion-title">{title}</h4>
          {description && <p className="af-properties-tab__accordion-description">{description}</p>}
        </div>
      </button>
      {isOpen && (
        <div className="af-properties-tab__accordion-content">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Properties Tab - Edit selected control properties
 */
const SLOT_LABELS: Record<keyof ControlStyleSet, string> = {
  containerStyle: 'Container',
  headerStyle: 'Header',
  bodyStyle: 'Body',
  contentStyle: 'Content',
  buttonStyle: 'Button',
};

export const PropertiesTab: React.FC<PropertiesTabProps> = ({ control, onUpdateControl, hideFullscreen, numberOfColumns }) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCompact, setIsCompact] = useState(true);
  const [isHovering, setIsHovering] = useState(false);
  const [openStylePanel, setOpenStylePanel] = useState<{
    slot: string;
    anchorEl: HTMLElement;
  } | null>(null);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
    setIsCompact(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setTimeout(() => {
      if (!isHovering) {
        setIsCompact(true);
      }
    }, 200);
  }, [isHovering]);

  const handleBasicChange = useCallback(
    (key: keyof FormControl, value: unknown) => {
      if (!control) return;
      onUpdateControl(control.id, { [key]: value });
    },
    [control, onUpdateControl]
  );

  const handleValidationChange = useCallback(
    (key: keyof FieldValidation, value: unknown) => {
      if (!control) return;
      onUpdateControl(control.id, {
        validation: {
          ...control.validation,
          [key]: value,
        },
      });
    },
    [control, onUpdateControl]
  );

  // Filter helper for search functionality
  const matchesSearch = useCallback((title: string, description?: string): boolean => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const titleMatch = title.toLowerCase().includes(query);
    const descMatch = description?.toLowerCase().includes(query) || false;
    return titleMatch || descMatch;
  }, [searchQuery]);

  const handleConfigChange = useCallback(
    (key: string, value: unknown) => {
      if (!control) return;
      onUpdateControl(control.id, {
        config: {
          ...control.config,
          [key]: value,
        },
      });
    },
    [control, onUpdateControl]
  );

  if (!control) {
    return (
      <div className="af-properties-tab af-properties-tab--empty" data-theme={theme}>
        <div className="af-properties-tab__empty-state">
          <p>No control selected</p>
          <p className="af-properties-tab__empty-hint">Select a control from the Design tab to edit its properties</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`af-properties-tab ${isCompact ? 'af-properties-tab--compact' : ''}`}
        data-theme={theme}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="af-properties-tab__header">
          <h3 className="af-properties-tab__section-title">
            {control.label || control.id}
          </h3>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <button
              className="af-properties-tab__fullscreen-button"
              onClick={() => setSearchQuery('')}
              aria-label="Toggle search"
              title="Search"
              style={{ padding: '4px 6px' }}
            >
              <SearchIcon width={14} height={14} />
            </button>
            <button
              className="af-properties-tab__fullscreen-button"
              onClick={() => setIsCompact(!isCompact)}
              aria-label="Toggle expand"
              title={isCompact ? "Expand" : "Collapse"}
              style={{ padding: '4px 6px' }}
            >
              {isCompact ? <ExpandPanelIcon width={14} height={14} /> : <CollapsePanelIcon width={14} height={14} />}
            </button>
            {!hideFullscreen && (
              <button
                className="af-properties-tab__fullscreen-button"
                onClick={() => setIsFullscreenOpen(true)}
                aria-label="Open properties editor in fullscreen"
                title="Fullscreen (Opens in modal)"
              >
                <FullscreenIcon
                  isFullscreen={false}
                  size={16}
                  color="currentColor"
                />
              </button>
            )}
          </div>
        </div>
        <div className="af-properties-tab__search">
          <input
            type="text"
            className="af-properties-tab__search-input"
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search properties"
          />
          {searchQuery && (
            <button
              className="af-properties-tab__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <CloseIcon width={12} height={12} />
            </button>
          )}
        </div>
        <div className="af-properties-tab__content">

        {/* Basic Properties */}
        {matchesSearch("Control Basics", "Field ID, label, placeholder, and help text") && (
        <AccordionSection
          title="Control Basics"
          description="Field ID, label, placeholder, and help text"
          defaultOpen={true}
          storageKey={`accordion-${control.id}-basic`}
          icon={<BasicPropertiesIcon size={16} color="#666" />}
        >

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              ID
              <input
                type="text"
                value={control.id}
                disabled
                className="af-properties-tab__input"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Type
              <input
                type="text"
                value={control.type}
                disabled
                className="af-properties-tab__input"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Label
              <ClearableInput
                type="text"
                value={control.label || ''}
                onChange={(e) => handleBasicChange('label', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Control label"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Placeholder
              <ClearableInput
                type="text"
                value={control.placeholder || ''}
                onChange={(e) => handleBasicChange('placeholder', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Placeholder text"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Help Text
              <ClearableInput
                type="text"
                value={control.helpText || ''}
                onChange={(e) => handleBasicChange('helpText', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Help text"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Description
              <textarea
                value={control.description || ''}
                onChange={(e) => handleBasicChange('description', e.target.value)}
                className="af-properties-tab__textarea"
                placeholder="Control description"
                rows={3}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* State Properties */}
        {matchesSearch("Control State", "Select one state for this control") && (
        <AccordionSection
          title="Control State"
          description="Select one state for this control"
          storageKey={`accordion-${control.id}-state`}
          icon={<StateIcon size={16} color="#666" />}
        >

          <div className="af-properties-tab__radio-group">
            <label className="af-properties-tab__radio-label">
              <input
                type="radio"
                name={`control-state-${control.id}`}
                checked={!control.required && !control.readonly && !control.disabled && !control.hidden}
                onChange={() => {
                  onUpdateControl(control.id, {
                    required: false,
                    readonly: false,
                    disabled: false,
                    hidden: false,
                  });
                }}
              />
              None
            </label>
          </div>

          <div className="af-properties-tab__radio-group">
            <label className="af-properties-tab__radio-label">
              <input
                type="radio"
                name={`control-state-${control.id}`}
                checked={control.required || false}
                onChange={() => {
                  onUpdateControl(control.id, {
                    required: true,
                    readonly: false,
                    disabled: false,
                    hidden: false,
                  });
                }}
              />
              Required
            </label>
          </div>

          <div className="af-properties-tab__radio-group">
            <label className="af-properties-tab__radio-label">
              <input
                type="radio"
                name={`control-state-${control.id}`}
                checked={control.readonly || false}
                onChange={() => {
                  onUpdateControl(control.id, {
                    required: false,
                    readonly: true,
                    disabled: false,
                    hidden: false,
                  });
                }}
              />
              Read Only
            </label>
          </div>

          <div className="af-properties-tab__radio-group">
            <label className="af-properties-tab__radio-label">
              <input
                type="radio"
                name={`control-state-${control.id}`}
                checked={control.disabled || false}
                onChange={() => {
                  onUpdateControl(control.id, {
                    required: false,
                    readonly: false,
                    disabled: true,
                    hidden: false,
                  });
                }}
              />
              Disabled
            </label>
          </div>

          <div className="af-properties-tab__radio-group">
            <label className="af-properties-tab__radio-label">
              <input
                type="radio"
                name={`control-state-${control.id}`}
                checked={control.hidden || false}
                onChange={() => {
                  onUpdateControl(control.id, {
                    required: false,
                    readonly: false,
                    disabled: false,
                    hidden: true,
                  });
                }}
              />
              Hidden
            </label>
          </div>
        </AccordionSection>
        )}

        {/* Mode Visibility */}
        {matchesSearch("Mode Visibility", "Show or hide this control per operating mode") && (
        <AccordionSection
          title="Mode Visibility"
          description="Show or hide this control per operating mode"
          storageKey={`accordion-${control.id}-mode-visibility`}
          icon={<StateIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Base Visibility
              <select
                value={control.visibility ?? ''}
                onChange={(e) =>
                  handleBasicChange(
                    'visibility',
                    e.target.value ? (e.target.value as 'edit-only' | 'view-only') : undefined,
                  )
                }
                className="af-properties-tab__select"
              >
                <option value="">Always visible</option>
                <option value="edit-only">Edit mode only (hidden in view/preview)</option>
                <option value="view-only">View mode only (hidden in edit)</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Screen Size
              <select
                value={control.responsiveVisibility ?? ''}
                onChange={(e) =>
                  handleBasicChange(
                    'responsiveVisibility',
                    (e.target.value as 'large-only' | 'small-only') || undefined,
                  )
                }
                className="af-properties-tab__select"
              >
                <option value="">Both (all screens)</option>
                <option value="large-only">Large screens only (hidden on mobile)</option>
                <option value="small-only">Small screens only (hidden on desktop)</option>
              </select>
            </label>
          </div>

          <p style={{ margin: '4px 0 8px', fontSize: 11, color: '#888' }}>
            Per-mode overrides — set to override Base Visibility for a specific mode.
          </p>

          {(['edit', 'view', 'design', 'admin', 'preview'] as const).map((mode) => {
            const current = control.modeVisibilitySettings?.[mode];
            const strVal = current === true ? 'show' : current === false ? 'hide' : '';
            return (
              <div key={mode} className="af-properties-tab__group" style={{ marginBottom: 6 }}>
                <label className="af-properties-tab__label" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <span style={{ textTransform: 'capitalize', minWidth: 56 }}>{mode}</span>
                  <select
                    value={strVal}
                    onChange={(e) => {
                      const val = e.target.value === 'show' ? true : e.target.value === 'hide' ? false : undefined;
                      const updated = { ...control.modeVisibilitySettings };
                      if (val === undefined) {
                        delete updated[mode];
                      } else {
                        updated[mode] = val;
                      }
                      onUpdateControl(control.id, {
                        modeVisibilitySettings: Object.keys(updated).length ? updated : undefined,
                      });
                    }}
                    className="af-properties-tab__select"
                    style={{ flex: 1 }}
                  >
                    <option value="">Inherit</option>
                    <option value="show">Show</option>
                    <option value="hide">Hide</option>
                  </select>
                </label>
              </div>
            );
          })}
        </AccordionSection>
        )}

        {/* Layout Properties */}
        {matchesSearch("Control Positioning", "Width, column/row span, and grid positioning") && (
        <AccordionSection
          title="Control Positioning"
          description="Width, column/row span, and grid positioning"
          storageKey={`accordion-${control.id}-layout`}
          icon={<GridLayoutIcon size={16} color="#666" />}
        >

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Width
              <select
                value={control.width || 'full'}
                onChange={(e) => handleBasicChange('width', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="full">Full Width</option>
                <option value="half">Half Width</option>
                <option value="third">Third Width</option>
                <option value="quarter">Quarter Width</option>
              </select>
            </label>
            {(control.width === 'half' || control.width === 'third' || control.width === 'quarter') && (numberOfColumns ?? 1) < 2 ? (
              <p role="alert" style={{ fontSize: 11, color: '#b45309', margin: '4px 0 0', fontWeight: 600 }}>
                This width has no effect: the form has 1 column. Set Form Properties &gt; Columns to 2 or more.
              </p>
            ) : (
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>
                Half and third widths only take effect when the form has 2 or more columns (Form Properties &gt; Columns).
              </p>
            )}
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Column Span
              <ClearableInput
                type="number"
                min="1"
                max="12"
                value={control.columnSpan || 12}
                onChange={(e) => handleBasicChange('columnSpan', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-properties-tab__input"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Row Span
              <ClearableInput
                type="number"
                min="1"
                value={control.rowSpan || 1}
                onChange={(e) => handleBasicChange('rowSpan', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-properties-tab__input"
              />
            </label>
          </div>

          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.forceNewRow || false}
                onChange={(e) => handleBasicChange('forceNewRow', e.target.checked)}
              />
              Force New Row
            </label>
          </div>
        </AccordionSection>
        )}

        {/* Validation Properties */}
        {matchesSearch("Field Validation", "Min/max length, pattern, and validation rules") && (
        <AccordionSection
          title="Field Validation"
          description="Min/max length, pattern, and validation rules"
          storageKey={`accordion-${control.id}-validation`}
          icon={<ValidationIcon size={16} color="#666" />}
        >

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Min Length
              <ClearableInput
                type="number"
                min="0"
                value={control.validation?.minLength || ''}
                onChange={(e) => handleValidationChange('minLength', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-properties-tab__input"
                placeholder="No minimum"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Max Length
              <ClearableInput
                type="number"
                min="0"
                value={control.validation?.maxLength || ''}
                onChange={(e) => handleValidationChange('maxLength', e.target.value ? parseInt(e.target.value) : undefined)}
                className="af-properties-tab__input"
                placeholder="No maximum"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Min Value
              <ClearableInput
                type="number"
                value={control.validation?.min !== undefined ? control.validation.min : ''}
                onChange={(e) => handleValidationChange('min', e.target.value ? parseFloat(e.target.value) : undefined)}
                className="af-properties-tab__input"
                placeholder="No minimum"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Max Value
              <ClearableInput
                type="number"
                value={control.validation?.max !== undefined ? control.validation.max : ''}
                onChange={(e) => handleValidationChange('max', e.target.value ? parseFloat(e.target.value) : undefined)}
                className="af-properties-tab__input"
                placeholder="No maximum"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Pattern (Regex)
              <ClearableInput
                type="text"
                value={control.validation?.pattern || ''}
                onChange={(e) => handleValidationChange('pattern', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Regular expression"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Error Message
              <ClearableInput
                type="text"
                value={control.validation?.message || ''}
                onChange={(e) => handleValidationChange('message', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Validation error message"
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* Default Value */}
        {matchesSearch("Initial Value", "Default or pre-filled value for the field") && (
        <AccordionSection
          title="Initial Value"
          description="Default or pre-filled value for the field"
          storageKey={`accordion-${control.id}-default`}
          icon={<DefaultValueIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Value
              <ClearableInput
                type="text"
                value={String(control.defaultValue || '')}
                onChange={(e) => handleBasicChange('defaultValue', e.target.value)}
                className="af-properties-tab__input"
                placeholder="Default value"
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* Data Binding */}
        {matchesSearch("Data Binding", "Source, path, and expression for dynamic field values") && (
        <AccordionSection
          title="Data Binding"
          description="Source, path, and expression for dynamic field values"
          storageKey={`accordion-${control.id}-binding`}
          icon={<DefaultValueIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Source
              <select
                value={control.binding?.source || ''}
                onChange={(e) =>
                  handleBasicChange('binding', {
                    ...control.binding,
                    source: e.target.value || undefined,
                  })
                }
                className="af-properties-tab__select"
              >
                <option value="">— none —</option>
                <option value="$json">$json — incoming JSON payload</option>
                <option value="$context">$context — workflow context</option>
                <option value="$api">$api — external API call</option>
                <option value="$form">$form — another field in this form</option>
              </select>
            </label>
            <p className="af-properties-tab__hint">Where to read the value from when the form loads.</p>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Path
              <ClearableInput
                type="text"
                value={control.binding?.path || ''}
                onChange={(e) =>
                  handleBasicChange('binding', {
                    ...control.binding,
                    path: e.target.value || undefined,
                  })
                }
                className="af-properties-tab__input"
                placeholder="e.g. customer.address.city"
              />
            </label>
            <p className="af-properties-tab__hint">Dot-notation path into the source data.</p>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Expression
              <ClearableInput
                type="text"
                value={control.binding?.expression || ''}
                onChange={(e) =>
                  handleBasicChange('binding', {
                    ...control.binding,
                    expression: e.target.value || undefined,
                  })
                }
                className="af-properties-tab__input"
                placeholder={`e.g. {{ $json.total * 1.1 }}`}
              />
            </label>
            <p className="af-properties-tab__hint">JS expression — takes priority over Source + Path. Reference other fields by their ID.</p>
          </div>

          {(control.binding?.source || control.binding?.path || control.binding?.expression) && (
            <button
              type="button"
              className="af-properties-tab__option-remove"
              style={{ width: '100%', marginTop: 4 }}
              onClick={() => handleBasicChange('binding', undefined)}
            >
              Clear Binding
            </button>
          )}
        </AccordionSection>
        )}

        {/* ─── 9 CAPABILITY GROUPS FROM STYLING SCHEMA ──────────────────── */}

        {/* 1. Styling */}
        {matchesSearch("CSS Styling", "Container and content CSS class customization") && (
        <AccordionSection
          title="CSS Styling"
          description="Container and content CSS class customization"
          storageKey={`accordion-${control.id}-styling`}
          icon={<StylingIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Container CSS Classes
              <textarea
                value={
                  control.styling?.styleBlock?.cssClasses?.container
                    ? JSON.stringify(control.styling.styleBlock.cssClasses.container, null, 2)
                    : ''
                }
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '{}');
                    onUpdateControl(control.id, {
                      styling: {
                        ...control.styling,
                        styleBlock: {
                          ...control.styling?.styleBlock,
                          cssClasses: {
                            ...control.styling?.styleBlock?.cssClasses,
                            container: parsed,
                          } as any,
                        } as any,
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for CSS classes');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Container CSS classes (JSON)"
                rows={4}
              />
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Content CSS Classes
              <textarea
                value={
                  control.styling?.styleBlock?.cssClasses?.content
                    ? JSON.stringify(control.styling.styleBlock.cssClasses.content, null, 2)
                    : ''
                }
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '{}');
                    onUpdateControl(control.id, {
                      styling: {
                        ...control.styling,
                        styleBlock: {
                          ...control.styling?.styleBlock,
                          cssClasses: {
                            ...control.styling?.styleBlock?.cssClasses,
                            content: parsed,
                          } as any,
                        } as any,
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for CSS classes');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Content CSS classes (JSON)"
                rows={4}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 1b. Visual Styles (StyleBuilderPanel) */}
        {matchesSearch("Visual Styles", "Per-slot visual style overrides for this control") && (
        <AccordionSection
          title="Visual Styles"
          description="Per-slot visual style overrides for this control"
          storageKey={`accordion-${control.id}-visual-styles`}
          icon={<StylingIcon size={16} color="#666" />}
        >
          {(() => {
            const applicableSlots = getApplicableSlots(control.type);
            const currentStyles = control.styles ?? {};
            return (
              <div style={{ padding: '4px 0' }}>
                {applicableSlots.map((slot) => (
                  <StyleSlotRow
                    key={slot}
                    slotKey={slot}
                    slotLabel={SLOT_LABELS[slot]}
                    value={currentStyles[slot]}
                    onOpen={(s, el) => setOpenStylePanel({ slot: s, anchorEl: el })}
                    onClear={(s) => {
                      const updated = { ...currentStyles };
                      delete updated[s as keyof ControlStyleSet];
                      onUpdateControl(control.id, {
                        styles: Object.keys(updated).length > 0 ? updated : undefined,
                      });
                    }}
                    isDark={isDark}
                  />
                ))}
              </div>
            );
          })()}
        </AccordionSection>
        )}

        {/* 2. Animations */}
        {matchesSearch("Animation Effects", "Control entry animations, speed, and delay") && (
        <AccordionSection
          title="Animation Effects"
          description="Control entry animations, speed, and delay"
          storageKey={`accordion-${control.id}-animations`}
          icon={<AnimationsIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Container Animation
              <ClearableInput
                type="text"
                value={control.animations?.container?.animation || ''}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    animations: {
                      ...control.animations,
                      container: {
                        ...control.animations?.container,
                        animation: e.target.value,
                        speed: control.animations?.container?.speed || 'normal',
                        enabled: true,
                        isCustom: false,
                      },
                    },
                  })
                }
                className="af-properties-tab__input"
                placeholder="e.g., fadeIn, slideInLeft"
              />
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Speed
              <select
                value={control.animations?.container?.speed || 'normal'}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    animations: {
                      ...control.animations,
                      container: {
                        ...control.animations?.container,
                        animation: control.animations?.container?.animation || '',
                        speed: e.target.value as any,
                        enabled: true,
                        isCustom: false,
                      },
                    },
                  })
                }
                className="af-properties-tab__select"
              >
                <option value="fast">Fast (0.3s)</option>
                <option value="normal">Normal (0.6s)</option>
                <option value="slow">Slow (1.2s)</option>
              </select>
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 3. JavaScript Blocks */}
        {matchesSearch("Custom Scripts", "JavaScript blocks with trust levels and execution triggers") && (
        <AccordionSection
          title="Custom Scripts"
          description="JavaScript blocks with trust levels and execution triggers"
          storageKey={`accordion-${control.id}-scripts`}
          icon={<ScriptsIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Trust Level
              <select
                value={control.scripting?.trustLevel || 0}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    scripting: {
                      ...control.scripting,
                      trustLevel: parseInt(e.target.value) as any,
                    },
                  })
                }
                className="af-properties-tab__select"
              >
                <option value={0}>0 - Trusted (Scripts execute)</option>
                <option value={1}>1 - Untrusted (Scripts blocked)</option>
                <option value={2}>2 - Explicit Trust (Requires approval)</option>
              </select>
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Scripts (JSON)
              <textarea
                value={control.scripting?.scripts ? JSON.stringify(control.scripting.scripts, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '[]');
                    onUpdateControl(control.id, {
                      scripting: {
                        ...control.scripting,
                        scripts: parsed,
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for scripts');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Scripts array (JSON)"
                rows={6}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 4. Container Settings */}
        {matchesSearch("Visual Container", "Border, header, padding, and visual wrapping") && (
        <AccordionSection
          title="Visual Container"
          description="Border, header, padding, and visual wrapping"
          storageKey={`accordion-${control.id}-container`}
          icon={<ContainerIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.container?.showBorder || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    container: {
                      ...control.container,
                      showBorder: e.target.checked,
                    },
                  })
                }
              />
              Show Border
            </label>
          </div>
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.container?.showHeader || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    container: {
                      ...control.container,
                      showHeader: e.target.checked,
                    },
                  })
                }
              />
              Show Header
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Header Text
              <ClearableInput
                type="text"
                value={control.container?.headerText || ''}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    container: {
                      ...control.container,
                      headerText: e.target.value,
                    },
                  })
                }
                className="af-properties-tab__input"
                placeholder="Header text"
              />
            </label>
          </div>
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.container?.showPadding || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    container: {
                      ...control.container,
                      showPadding: e.target.checked,
                    },
                  })
                }
              />
              Show Padding
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 5. Custom Actions */}
        {matchesSearch("Event Actions", "Custom actions triggered by user events or conditions") && (
        <AccordionSection
          title="Event Actions"
          description="Custom actions triggered by user events or conditions"
          storageKey={`accordion-${control.id}-actions`}
          icon={<ActionsIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Trust Level
              <select
                value={control.actions?.trustLevel || 0}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    actions: {
                      ...control.actions,
                      trustLevel: parseInt(e.target.value) as any,
                    },
                  })
                }
                className="af-properties-tab__select"
              >
                <option value={0}>0 - Trusted</option>
                <option value={1}>1 - Untrusted (Safe only)</option>
                <option value={2}>2 - Explicit Trust</option>
              </select>
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Actions (JSON)
              <textarea
                value={control.actions?.actions ? JSON.stringify(control.actions.actions, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '[]');
                    onUpdateControl(control.id, {
                      actions: {
                        ...control.actions,
                        actions: parsed,
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for actions');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Actions array (JSON)"
                rows={6}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 6. Internationalization */}
        {matchesSearch("Translations", "Multi-language labels, help text, and locale settings") && (
        <AccordionSection
          title="Translations"
          description="Multi-language labels, help text, and locale settings"
          storageKey={`accordion-${control.id}-i18n`}
          icon={<I18nIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Current Language
              <ClearableInput
                type="text"
                value={control.i18n?.currentLanguage || 'en'}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    i18n: {
                      ...control.i18n,
                      currentLanguage: e.target.value,
                    },
                  })
                }
                className="af-properties-tab__input"
                placeholder="e.g., en, es, fr"
              />
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Label (JSON)
              <textarea
                value={
                  control.i18n?.config?.label ? JSON.stringify(control.i18n.config.label, null, 2) : ''
                }
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '{}');
                    onUpdateControl(control.id, {
                      i18n: {
                        ...control.i18n,
                        config: {
                          ...control.i18n?.config,
                          label: parsed,
                        },
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for label');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Label translations"
                rows={4}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 7. Icons */}
        {matchesSearch("Icon Settings", "Prefix/suffix icons, positioning, and styling") && (
        <AccordionSection
          title="Icon Settings"
          description="Prefix/suffix icons, positioning, and styling"
          storageKey={`accordion-${control.id}-icons`}
          icon={<IconsIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.icons?.enabled || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    icons: {
                      ...control.icons,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              Enable Icons
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Icons (JSON)
              <textarea
                value={control.icons?.icons ? JSON.stringify(control.icons.icons, null, 2) : '{}'}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value || '{}');
                    onUpdateControl(control.id, {
                      icons: {
                        ...control.icons,
                        icons: parsed,
                      },
                    });
                  } catch (err) {
                    console.warn('Invalid JSON for icons');
                  }
                }}
                className="af-properties-tab__textarea"
                placeholder="Icons configuration (JSON)"
                rows={6}
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 8. Accessibility */}
        {matchesSearch("WCAG Compliance", "ARIA labels, keyboard support, and screen reader configuration") && (
        <AccordionSection
          title="WCAG Compliance"
          description="ARIA labels, keyboard support, and screen reader configuration"
          storageKey={`accordion-${control.id}-a11y`}
          icon={<AccessibilityIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.accessibility?.enabled || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    accessibility: {
                      ...control.accessibility,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              Enable Accessibility
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              WCAG Level
              <select
                value={control.accessibility?.config?.wcagLevel || 'AA'}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    accessibility: {
                      ...control.accessibility,
                      config: {
                        ...control.accessibility?.config,
                        wcagLevel: e.target.value as any,
                      } as any,
                    },
                  })
                }
                className="af-properties-tab__select"
              >
                <option value="A">WCAG A</option>
                <option value="AA">WCAG AA (Recommended)</option>
                <option value="AAA">WCAG AAA</option>
              </select>
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              ARIA Label
              <ClearableInput
                type="text"
                value={control.accessibility?.config?.aria?.label || ''}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    accessibility: {
                      ...control.accessibility,
                      config: {
                        ...control.accessibility?.config,
                        aria: {
                          ...control.accessibility?.config?.aria,
                          label: e.target.value,
                        },
                      } as any,
                    },
                  })
                }
                className="af-properties-tab__input"
                placeholder="ARIA label"
              />
            </label>
          </div>
        </AccordionSection>
        )}

        {/* 9. Security */}
        {matchesSearch("Trust & Security", "Trust levels, script execution audit, and action approval") && (
        <AccordionSection
          title="Trust & Security"
          description="Trust levels, script execution audit, and action approval"
          storageKey={`accordion-${control.id}-security`}
          icon={<SecurityIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.security?.enabled || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    security: {
                      ...control.security,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
              Enable Security
            </label>
          </div>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Trust Level
              <select
                value={control.security?.config?.trustLevel || 0}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    security: {
                      ...control.security,
                      config: {
                        ...control.security?.config,
                        trustLevel: parseInt(e.target.value) as any,
                      },
                    },
                  })
                }
                className="af-properties-tab__select"
              >
                <option value={0}>0 - Trusted (Full access)</option>
                <option value={1}>1 - Untrusted (Blocked scripts)</option>
                <option value={2}>2 - Explicit Trust (Requires approval)</option>
              </select>
            </label>
          </div>
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.security?.config?.auditScriptExecution || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    security: {
                      ...control.security,
                      config: {
                        ...control.security?.config,
                        auditScriptExecution: e.target.checked,
                      } as any,
                    },
                  })
                }
              />
              Audit Script Execution
            </label>
          </div>
          <div className="af-properties-tab__checkbox-group">
            <label className="af-properties-tab__checkbox-label">
              <input
                type="checkbox"
                checked={control.security?.config?.auditActionExecution || false}
                onChange={(e) =>
                  onUpdateControl(control.id, {
                    security: {
                      ...control.security,
                      config: {
                        ...control.security?.config,
                        auditActionExecution: e.target.checked,
                      } as any,
                    },
                  })
                }
              />
              Audit Action Execution
            </label>
          </div>
        </AccordionSection>
        )}

        {/* Control Inheritance */}
        {matchesSearch("Control Inheritance", "Inherit or import controls from a base form") && (
        <AccordionSection
          title="Control Inheritance"
          description="Inherit or import controls from a base form"
          storageKey={`accordion-${control.id}-inheritance`}
          icon={<BasicPropertiesIcon size={16} color="#666" />}
        >
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Referred Form
              <ConnectedFormLookupField
                value={control.referredFormID}
                onChange={(formID) => {
                  onUpdateControl(control.id, {
                    referredFormID: formID,
                    ...(formID !== control.referredFormID ? { referredControlID: undefined } : {}),
                  });
                }}
                onEdit={(formID) => {
                  const params = new URLSearchParams({ view: 'edit', formID: String(formID) });
                  window.open(`http://localhost:6121/?${params.toString()}`, '_blank', 'noopener');
                }}
                editTitle="Design this form — opens in Forms Studio"
                placeholder="Search forms…"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Referred Form Path
              <ClearableInput
                type="text"
                value={control.referredFormPath ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim() || undefined;
                  if (v && !/^(https?|ipfs):\/\//i.test(v)) return; // only allow safe URLs
                  handleBasicChange('referredFormPath', v);
                }}
                className="af-properties-tab__input"
                placeholder="ipfs://Qm… or https://cdn.example.com/form.json"
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Referred Controls
              <ControlSelect
                formID={control.referredFormID}
                value={Array.isArray(control.referredControlID) ? control.referredControlID : control.referredControlID ? [control.referredControlID] : []}
                onChange={(controlIDs, parentControls) => {
                  // Merge parent control properties into child control
                  if (controlIDs.length > 0 && parentControls.length > 0) {
                    const parentControl = parentControls[0];
                    // Merge parent control's config and other properties
                    const updates: Record<string, any> = {
                      referredControlID: controlIDs,
                      // Merge parent control's config into child
                      config: {
                        ...(parentControl.config || {}),
                        ...((control as any).config || {}),
                      },
                    };
                    // Copy other parent properties (type, validation, etc.) if child doesn't have them
                    if (!control.type && parentControl.type) {
                      updates.type = parentControl.type;
                    }
                    if (!control.validation && (parentControl as any).validation) {
                      updates.validation = (parentControl as any).validation;
                    }
                    onUpdateControl(control.id, updates);
                  } else {
                    onUpdateControl(control.id, {
                      referredControlID: controlIDs.length > 0 ? controlIDs : undefined,
                    });
                  }
                }}
              />
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Inherit Type
              <select
                value={control.controlInheritType ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleBasicChange(
                    'controlInheritType',
                    v === 'override' || v === 'import' ? v : undefined,
                  );
                }}
                className="af-properties-tab__select"
              >
                <option value="">— none —</option>
                <option value="override">override — merge base control into this one</option>
                <option value="import">import — replace placeholder with base control(s)</option>
              </select>
            </label>
          </div>
        </AccordionSection>
        )}

        {/* ── Control Configuration (type-specific) ─────────────────────── */}
        {matchesSearch("Type Configuration", "Control type-specific settings and options") && (
        <AccordionSection
          title="Type Configuration"
          description="Control type-specific settings and options"
          storageKey={`accordion-${control.id}-config`}
          icon={<ControlConfigIcon size={16} color="#666" />}
        >
          <ControlConfig
            control={control}
            onConfigChange={handleConfigChange}
            onBasicChange={handleBasicChange}
            onValidationChange={handleValidationChange}
            onControlChange={(updates) => onUpdateControl(control.id, updates)}
          />
        </AccordionSection>
        )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <PropertiesModal
        control={control}
        onUpdateControl={onUpdateControl}
        isOpen={isFullscreenOpen}
        onClose={() => setIsFullscreenOpen(false)}
      />

      {/* Style Builder Panel — renders via portal, no layout impact */}
      {openStylePanel && (
        <StyleBuilderPanel
          slot={openStylePanel.slot}
          slotLabel={SLOT_LABELS[openStylePanel.slot as keyof ControlStyleSet]}
          value={control.styles?.[openStylePanel.slot as keyof ControlStyleSet] ?? {}}
          anchorEl={openStylePanel.anchorEl}
          onApply={(newStyles) => {
            onUpdateControl(control.id, {
              styles: {
                ...control.styles,
                [openStylePanel.slot]: newStyles,
              },
            });
          }}
          onClose={() => setOpenStylePanel(null)}
          isDark={isDark}
        />
      )}
    </>
  );
};

PropertiesTab.displayName = 'PropertiesTab';
